import { useEffect, useState, useRef } from 'react';
import { useWriteContract, useWaitForTransactionReceipt, useBalance, useReadContract, useAccount } from 'wagmi';
import { contractAddress, usdcAddress } from '../../config/contract';
import { abi } from '../../abi';
import { abiBSC } from '../../abiBSC';
import { parseUnits } from 'viem';

// IMPROVED: Convert from human-readable amount to token units
// For example: 9.46 -> 9460000 (for USDT/USDC with 6 decimals)
function safeParseUnits(amount: string, decimals: number): bigint {
  try {
    // Force 6 decimals for USDC/USDT on all chains
    const fixedDecimals = 6;
    
    // Remove any commas, spaces etc
    amount = amount.replace(/,/g, '').trim();
    
    // Make sure we only have one decimal point
    const parts = amount.split('.');
    if (parts.length > 2) {
      throw new Error('Invalid number format');
    }
    
    // Handle case with no decimal point
    if (parts.length === 1) {
      // Integer - add required zeros
      const result = BigInt(parts[0] + '0'.repeat(fixedDecimals));
      console.log(`safeParseUnits: Whole number ${amount} -> ${result.toString()}`);
      return result;
    }
    
    const integerPart = parts[0];
    let fractionalPart = parts[1];
    
    // If fractional part is longer than decimals, truncate it
    if (fractionalPart.length > fixedDecimals) {
      fractionalPart = fractionalPart.substring(0, fixedDecimals);
    } 
    // If shorter than decimals, pad with zeros
    else if (fractionalPart.length < fixedDecimals) {
      fractionalPart = fractionalPart + '0'.repeat(fixedDecimals - fractionalPart.length);
    }
    
    // Check if number is pure 0
    if (integerPart === '0' && /^0*$/.test(fractionalPart)) {
      return BigInt(0);
    }
    
    // Remove leading zeros from integer part (except if it's just "0")
    const cleanIntegerPart = integerPart === '0' ? '0' : integerPart.replace(/^0+/, '');
    
    // Combine parts into final string and convert to BigInt
    const amountInSmallestUnit = cleanIntegerPart + fractionalPart;
    const result = BigInt(amountInSmallestUnit);
    console.log(`safeParseUnits: Decimal ${amount} -> ${result.toString()}`);
    return result;
  } catch (error) {
    console.error('Error in safeParseUnits:', error);
    return BigInt(0);
  }
}

// CRITICAL: New specialized function for this specific contract
// Send tokens with their native decimals - ETH uses 6, BSC uses 18
function formatTokenAmount(amountStr: string, network: 'eth' | 'bsc'): bigint {
  try {
    // Remove any non-numeric characters except the decimal point
    const cleanAmount = amountStr.replace(/[^0-9.]/g, '');
    
    // ETH uses 6 decimals, BSC uses 18 decimals
    const decimals = network === 'bsc' ? 18 : 6;
    
    // Use parseUnits which handles decimal conversion correctly
    return parseUnits(cleanAmount, decimals);
  } catch (error) {
    console.error('Error in formatTokenAmount:', error);
    return BigInt(0);
  }
}

// Function to format a number to exactly N decimal places for display
function formatExactDecimal(amount: string, decimals: number): string {
  try {
    // Remove any commas, spaces etc
    amount = amount.replace(/,/g, '').trim();
    
    // Parse the float
    const num = parseFloat(amount);
    
    // Fix to exactly N decimals, using mathematical rounding
    return num.toFixed(decimals);
  } catch (error) {
    return "0.000000";
  }
}

interface UsdcPaymentProps {
  amount: string;
  userAddress?: string;
  referralAddress: string;
  stake: boolean;
  network: 'eth' | 'bsc';
  onError: (error: string) => void;
  onSuccess: (hash: string) => void;
  onProcessing: (processing: boolean) => void;
  showTransaction: (
    type: string, 
    message: string, 
    network: string, 
    hash: string,
    tokensReceived?: string,
    isApproval?: boolean,
    onBuyClick?: (() => void) | null
  ) => void;
}

export function UsdcPayment({
  amount,
  userAddress,
  referralAddress,
  stake,
  network,
  onError,
  onSuccess,
  onProcessing,
  showTransaction,
}: UsdcPaymentProps) {
  const tokenAddress = usdcAddress[network];
  const presaleAddress = contractAddress[network];
  
  // Add state to track approval status
  const [hasApproved, setHasApproved] = useState(false);
  
  // Create a reference to track processed transactions
  const processedTransactions = useRef<Set<string>>(new Set());
  
  // Add a reference to track onSuccess calls
  const successProcessed = useRef<Set<string>>(new Set());

  // Get current connected chain
  const { chain } = useAccount();

  const { data: usdcBalanceData } = useBalance({ 
    address: userAddress as `0x${string}`,
    token: tokenAddress
  });

  // Check allowance
  const { data: allowance } = useReadContract({
    address: tokenAddress,
    abi: [{
      inputs: [
        { name: 'owner', type: 'address' },
        { name: 'spender', type: 'address' }
      ],
      name: 'allowance',
      outputs: [{ type: 'uint256' }],
      stateMutability: 'view',
      type: 'function'
    }],
    functionName: 'allowance',
    args: [userAddress as `0x${string}`, presaleAddress]
  });

  const {
    data: approveHash,
    isPending: isApproving,
    writeContract: writeApprove
  } = useWriteContract();

  const {
    data: purchaseHash,
    isPending: isPurchasing,
    writeContract: writePurchase
  } = useWriteContract();

  const { isLoading: isConfirming, isSuccess: isApproveSuccess } = useWaitForTransactionReceipt({ 
    hash: approveHash,
    confirmations: 2
  });

  const { isLoading: isPurchaseConfirming, isSuccess: isPurchaseSuccess } = useWaitForTransactionReceipt({ 
    hash: purchaseHash,
    confirmations: 2
  });

  const needsApproval = (amount: string) => {
    // If we've already successfully approved, don't need to approve again
    if (hasApproved) return false;
    
    if (!allowance || !amount) return true;
    try {
      // Use our specialized formatter for token amounts
      const rawAmount = formatTokenAmount(amount, network);
      
      console.log('needsApproval check for USDC:', { 
        originalAmount: amount,
        calculatedRawAmount: rawAmount.toString(),
        allowance: allowance.toString()
      });
      
      return rawAmount > allowance;
    } catch (e) {
      console.error('Error in needsApproval:', e);
      return true;
    }
  };

  const handlePayment = async () => {
    if (!userAddress) {
      onError('Please connect your wallet first');
      return;
    }

    // Check if referrer address is valid for EVM
    if (referralAddress && !referralAddress.startsWith('0x')) {
      // Just log the error, don't show to user
      console.error('Invalid referrer address for USDC payments');
      return;
    }

    // Check for chain mismatch
    if (chain) {
      const expectedChainId = network === 'eth' ? 1 : 56; // 1 for ETH, 56 for BSC
      if (chain.id !== expectedChainId) {
        const currentNetworkName = chain.id === 1 ? 'Ethereum' : chain.id === 56 ? 'Binance Smart Chain' : 'Unknown';
        const expectedNetworkName = network === 'eth' ? 'Ethereum' : 'Binance Smart Chain';
        onError(`Network mismatch! Your wallet is connected to ${currentNetworkName}, but the application is set to ${expectedNetworkName}. Please switch networks in the application or in your wallet.`);
        return;
      }
    }

    const inputAmount = parseFloat(amount);
    if (!amount || isNaN(inputAmount) || inputAmount <= 0) {
      onError('Please enter a valid amount');
      return;
    }

    try {
      // Reset success ref for this transaction
      successProcessed.current = new Set();
      
      // Get contract address based on network (ETH or BSC)
      const presaleAddress = contractAddress[network];
      if (!presaleAddress) throw new Error(`Invalid network: ${network}`);

      onProcessing(true);

      console.log('USDC payment network:', network);

      // For USDC, we need to calculate decimals based on network
      const rawAmount = formatTokenAmount(amount, network);
      
      console.log(`========= USDC FINAL CALCULATION =========`);
      console.log(`Input amount string: "${amount}"`);
      console.log(`Raw token amount (${network === 'bsc' ? '18' : '6'} decimals): ${rawAmount.toString()}`);
      console.log(`=========================================`);
      
      // Debug logging to see what MetaMask will display
      const metaMaskDisplay = Number(rawAmount) / (network === 'bsc' ? 1e18 : 1e6);
      console.log(`MetaMask will display: ${metaMaskDisplay} USDC`);
      console.log(`The contract will receive: ${rawAmount.toString()} (${amount} USDC)`);
      
      // Use this raw value directly for the contract call
      if (usdcBalanceData && rawAmount > usdcBalanceData.value) {
        onError(`Insufficient USDC balance. You have ${usdcBalanceData.formatted} USDC`);
        return;
      }

      // First check if we need approval
      if (needsApproval(amount)) {
        try {
          // Use maximum possible approval to avoid repeated approvals
          // MAX_UINT256 value: 2^256 - 1
          const MAX_UINT256 = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff');
          
          // Calculate what the amount would be for debugging
          const [wholePart, decimalPart = ''] = amount.replace(/,/g, '').split('.');
          const paddedDecimal = decimalPart.padEnd(network === 'bsc' ? 18 : 6, '0')
                                           .slice(0, network === 'bsc' ? 18 : 6);
          const rawAmount = BigInt(wholePart + paddedDecimal);
          
          // Log exact values for debugging
          console.log('USDC Approval Request:', {
            spender: presaleAddress,
            amount: 'MAX_UINT256',
            amountHex: MAX_UINT256.toString(16),
            rawAmountForReference: rawAmount.toString(),
            tokenAddress
          });
          
          // Show clear UI message about approval
          showTransaction('pending', `Approving USDC for spending...`, network, '');
          
          // First approve USDC spending with unlimited approval
          await writeApprove({
            address: tokenAddress,
            abi: [{
              inputs: [
                { internalType: 'address', name: 'spender', type: 'address' },
                { internalType: 'uint256', name: 'amount', type: 'uint256' }
              ],
              name: 'approve',
              outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
              stateMutability: 'nonpayable',
              type: 'function'
            }],
            functionName: 'approve',
            args: [presaleAddress, MAX_UINT256],
          });

          // Show approval waiting and prevent immediate purchase
          if (approveHash) {
            showTransaction('pending', `Waiting for USDC approval to complete...`, network, approveHash, undefined, true, () => {
              // Setup callback for when "Buy Now" is clicked after approval
              const handleBuyAfterApproval = () => {
                // Convert amount string to BigInt with proper decimal handling
                const rawAmount = formatTokenAmount(amount, network);
                
                console.log(`Buy after approval clicked with amount: ${amount} USDC (${rawAmount.toString()})`);
                
                console.log(`========= USDC BUY AFTER APPROVAL =========`);
                console.log(`Input amount: ${amount}`);
                console.log(`Raw token amount (${network === 'bsc' ? '18' : '6'} decimals): ${rawAmount.toString()}`);
                console.log(`=========================================`);
                
                // Execute the purchase directly with raw amount
                writePurchase({
                  address: presaleAddress,
                  abi: network === 'bsc' ? abiBSC : abi,
                  functionName: 'buyWithUSDC',
                  args: [rawAmount, stake, referralAddress],
                  gas: network === 'eth' ? BigInt(250000) : undefined,
                  value: BigInt(0) // Explicitly set value to 0 for token transactions
                });
                
                // Show purchase pending popup if hash
                if (purchaseHash) {
                  // Ensure we format the amount consistently - always to 2 decimal places
                  const formattedAmount = parseFloat(amount).toFixed(2);
                  console.log(`Showing transaction popup with amount: ${formattedAmount} USDC`);
                  showTransaction('pending', `Purchasing $QGEN with ${formattedAmount} USDC...`, network, purchaseHash);
                }
              };
              
              // Mark as approved so we don't ask for approval again
              setHasApproved(true);
              
              // Show success message with Buy button in the popup
              showTransaction(
                'success', 
                'USDC successfully approved for spending. Click Buy Now to complete your purchase.', 
                network, 
                approveHash,
                undefined,  // No tokens received yet
                true,       // This is an approval transaction
                handleBuyAfterApproval // Safe callback for Buy button
              );
              
              // Reset processing state so user can proceed manually
              onProcessing(false);
            });
          }
          return;
        } catch (approvalError: any) {
          // Show proper error message
          console.error('USDC Approval failed:', approvalError);
          onError(approvalError.message || 'Approval failed. Please try again.');
          showTransaction('error', approvalError.message || 'Approval failed', network, '');
          onProcessing(false);
          return;
        }
      }

      // Then execute the purchase with the raw amount
      await writePurchase({
        address: presaleAddress,
        abi: network === 'bsc' ? abiBSC : abi,
        functionName: 'buyWithUSDC',
        args: [rawAmount, stake, referralAddress],
        gas: network === 'eth' ? BigInt(250000) : undefined,
        value: BigInt(0) // Explicitly set value to 0 for token transactions
      });
      
      // Log what's being sent
      console.log('USDC Purchase Request:', {
        originalAmount: amount,
        processedAmount: rawAmount.toString(),
        stake,
        referralAddress,
        targetContract: presaleAddress,
        network,
      });
      
      // Show purchase pending popup
      if (purchaseHash) {
        // Ensure we format the amount consistently - always to 2 decimal places
        const formattedAmount = parseFloat(amount).toFixed(2);
        console.log(`Showing transaction popup with amount: ${formattedAmount} USDC (raw: ${formatTokenAmount(amount, network).toString()})`);
        // Show pending transaction popup
        showTransaction(
          'pending', 
          `Purchasing $QGEN with ${formattedAmount} USDC...`, 
          network, 
          purchaseHash
        );
      }

    } catch (err: any) {
      console.error('USDC Transaction failed:', err);
      onError(err.message || 'Transaction failed');
      showTransaction('error', err.message || 'Transaction failed', network, '');
      // Reset processing state when error occurs
      onProcessing(false);
    }
  };

  // Update processing state
  useEffect(() => {
    onProcessing(isApproving || isPurchasing || isConfirming || isPurchaseConfirming);
  }, [isApproving, isPurchasing, isConfirming, isPurchaseConfirming, onProcessing]);

  // Helper function to extract tokens from log
  const extractTokensFromLog = async (log: any): Promise<string | undefined> => {
    try {
      console.log("Full log data being processed:", {
        address: log.address,
        topics: log.topics,
        data: log.data
      });
      
      // Skip the 0x prefix
      const dataWithoutPrefix = log.data.startsWith('0x') 
        ? log.data.slice(2) 
        : log.data;
      
      // First 64 chars after prefix are the token amount
      const tokensHexValue = '0x' + dataWithoutPrefix.slice(0, 64);
      
      // Convert to BigInt
      const tokensBigInt = BigInt(tokensHexValue);
      
      console.log('Raw USDC token value from log:', tokensBigInt.toString());
      
      // QGEN tokens are in 18 decimals in the contract (normalized)
      const tokensNumber = Number(tokensBigInt) / 10**18;
      console.log('Token calculation:', tokensBigInt.toString(), '/', '10^18', '=', tokensNumber);
      
      // Format with commas and 2 decimal places
      return tokensNumber.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } catch (error) {
      console.error("Error extracting tokens from USDC log:", error);
      return undefined;
    }
  };

  // Handle success
  useEffect(() => {
    let isActive = true;
    
    if (isPurchaseSuccess && purchaseHash && isActive) {
      // Check if we've already processed this success call
      if (successProcessed.current.has(purchaseHash)) {
        return;
      }
      
      // Mark as processed
      successProcessed.current.add(purchaseHash);
      
      // Force a cache invalidation and clear any Tanstack query cache related to this
      // This ensures fresh data will be fetched when onSuccess triggers refreshStats
      if (window.sessionStorage) {
        // Find and remove any queries related to contract reads
        const keys = Object.keys(window.sessionStorage);
        const contractQueryKeys = keys.filter(key => 
          key.includes('readContract') || 
          key.includes('stakingBalance') || 
          key.includes('pendingPurchase')
        );
        contractQueryKeys.forEach(key => {
          console.log('Clearing cached query:', key);
          window.sessionStorage.removeItem(key);
        });
      }
      
      // Call success callback once
      console.log('USDC transaction successful, calling onSuccess with hash:', purchaseHash);
      onSuccess(purchaseHash);
      
      // Show a basic success popup immediately (will be updated later with token amount)
      showTransaction(
        'success', 
        `Successfully purchased $QGEN with ${amount} USDC${stake ? ' and staked' : ''}. Calculating tokens received...`, 
        network, 
        purchaseHash,
        undefined, // No tokens yet, we'll update this
        false,
        null
      );
      
      // Add a delayed second callback to ensure data has settled in blockchain
      setTimeout(() => {
        if (isActive) {
          console.log('USDC transaction: Calling delayed onSuccess to ensure data refresh:', purchaseHash);
          onSuccess(purchaseHash);
        }
      }, 5000);
      
      // Get transaction receipt to extract token amount directly from the logs
      const fetchTokensFromReceipt = async () => {
        try {
          // Check if ethereum is available in window
          if (!window.ethereum) {
            throw new Error("MetaMask not available");
          }
          
          // Define type for the receipt logs
          interface TransactionLog {
            address: string;
            topics: string[];
            data: string;
            blockNumber: string;
            transactionHash: string;
            transactionIndex: string;
            blockHash: string;
            logIndex: string;
            removed: boolean;
          }
          
          // Access the actual receipt from wagmi with proper typing
          const receipt = await (window.ethereum as any).request({
            method: 'eth_getTransactionReceipt',
            params: [purchaseHash]
          });
          
          if (!receipt || !receipt.logs) {
            throw new Error("No transaction receipt or logs found");
          }
          
          console.log("USDC Transaction receipt:", {
            hash: purchaseHash,
            logCount: receipt.logs.length,
            status: receipt.status
          });
          
          // Log all events for debugging
          console.log("All event topics in transaction:", receipt.logs.map((log: TransactionLog, index: number) => ({
            index,
            address: log.address,
            topic0: log.topics[0],
            hasData: !!log.data && log.data !== '0x'
          })));
          
          // Topic hashes for different events
          const tokensBoughtTopic = "0x9c46ba47a94a38b7e008b5a7a4ae121852c03bd4d17b04e99e99e6a7c734ad2b";
          const tokensStakedTopic = "0xb539ca1e5c8d398ddf1c41c30166f33404941683be4683319b57669a93dad4ef";
          const transferTopic = "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef";
          
          // Try to find the TokensBought or TokensStaked event
          let relevantLog = receipt.logs.find((log: TransactionLog) => 
            log.topics[0]?.toLowerCase() === tokensBoughtTopic.toLowerCase()
          );
          
          // If TokensBought not found, check for TokensStaked event
          if (!relevantLog && stake) {
            relevantLog = receipt.logs.find((log: TransactionLog) => 
              log.topics[0]?.toLowerCase() === tokensStakedTopic.toLowerCase()
            );
            
            if (relevantLog) {
              console.log("Found TokensStaked event instead of TokensBought");
            }
          }
          
          // If we still don't have a relevant log, look for the last event in the logs
          if (!relevantLog && receipt.logs.length > 0) {
            // Try to find the contract address for our token contract to help identify events
            const contractAddressLower = presaleAddress.toLowerCase();
            
            // Loop through logs to find ones related to our contract
            let contractLogs = receipt.logs.filter((log: TransactionLog) => 
              log.address?.toLowerCase() === contractAddressLower
            );
            
            if (contractLogs.length > 0) {
              console.log("Found logs from our contract:", contractLogs.length);
              relevantLog = contractLogs[contractLogs.length - 1]; // Use the last contract log
            } else {
              console.log("No logs found from our contract, using last log");
              relevantLog = receipt.logs[receipt.logs.length - 1]; // Fallback to last log
            }
          }
          
          // If we have a relevant log, try to extract the token amount
          if (relevantLog) {
            console.log("Found relevant log:", {
              address: relevantLog.address,
              topic0: relevantLog.topics[0],
              dataPreview: relevantLog.data?.substring(0, 66) + "..."
            });
            
            // Extract tokens from the log
            const extractedTokens = await extractTokensFromLog(relevantLog);
            
            console.log("USDC Transaction processed, extracted tokens:", extractedTokens);
            
            // If we successfully extracted tokens, update the popup
            if (extractedTokens && isActive) {
          showTransaction(
            'success', 
                `Successfully purchased $QGEN with ${amount} USDC${stake ? ' and staked' : ''}`, 
            network, 
            purchaseHash,
                extractedTokens,
                false,
                null
          );
            }
          } else {
            console.log("No relevant log found for token extraction");
          }
        } catch (error) {
          console.error("Error fetching USDC transaction receipt:", error);
          // Basic success popup was already shown, so no need to show another
        }
      };
      
      // Execute async function with a delay to ensure transaction is fully processed
      setTimeout(() => {
        if (isActive) {
      fetchTokensFromReceipt();
        }
      }, 2000);
    }
    
    // Return cleanup function to prevent memory leaks
    return () => { isActive = false; };
  }, [isPurchaseSuccess, purchaseHash, onSuccess, amount, network, showTransaction, stake, presaleAddress]);

  // Reset approval flag when amount or network changes
  useEffect(() => {
    setHasApproved(false);
  }, [amount, network]);

  // Handle approval success with Buy button
  useEffect(() => {
    // Flag to prevent multiple executions
    if (isApproveSuccess && approveHash && !processedTransactions.current.has(approveHash)) {
      // Mark this transaction as processed to prevent loops
      processedTransactions.current.add(approveHash);
      
      console.log("USDC approval successful, showing Buy button...");
      
      // Mark as approved so we don't ask for approval again
      setHasApproved(true);
      
      // Create a safe callback that doesn't directly reference handlePayment
      const handleBuyAfterApproval = () => {
        // This is now a standalone function that will initiate a purchase
        // without creating circular dependencies
        console.log("Executing Buy after approval click...");
        
        try {
          // Use our specialized formatter for token amounts
          const rawAmount = formatTokenAmount(amount, network);
          
          console.log(`========= USDC BUY AFTER APPROVAL =========`);
          console.log(`Input amount: ${amount}`);
          console.log(`Raw token amount (${network === 'bsc' ? '18' : '6'} decimals): ${rawAmount.toString()}`);
          console.log(`=========================================`);
          
          // Execute the purchase directly with raw amount
          writePurchase({
            address: presaleAddress,
            abi: network === 'bsc' ? abiBSC : abi,
            functionName: 'buyWithUSDC',
            args: [rawAmount, stake, referralAddress],
            gas: network === 'eth' ? BigInt(250000) : undefined,
            value: BigInt(0) // Explicitly set value to 0 for token transactions
          });
      
          // Show purchase pending popup if hash
          if (purchaseHash) {
            // Ensure we format the amount consistently - always to 2 decimal places
            const formattedAmount = parseFloat(amount).toFixed(2);
            console.log(`Showing transaction popup with amount: ${formattedAmount} USDC (raw: ${rawAmount.toString()})`);
            showTransaction(
              'pending', 
              `Purchasing $QGEN with ${formattedAmount} USDC...`, 
              network, 
              purchaseHash
            );
          }
        } catch (err: any) {
          console.error('USDC Transaction failed:', err);
          onError(err.message || 'Transaction failed');
          showTransaction('error', err.message || 'Transaction failed', network, '');
          onProcessing(false);
        }
      };
      
      // Show success message with Buy button in the popup
      showTransaction(
        'success', 
        `Successfully approved USDC for spending. Click Buy Now to complete your purchase.`, 
        network, 
        approveHash,
        undefined,  // No tokens received yet
        true,       // This is an approval transaction
        handleBuyAfterApproval // Safe callback for Buy button
      );
      
      // Reset processing state so user can proceed manually
      onProcessing(false);
    }
  }, [isApproveSuccess, approveHash, amount, stake, referralAddress, network, presaleAddress, writePurchase, showTransaction, onProcessing, onError]);

  return { 
    handlePayment,
    needsApproval: needsApproval(amount),
    balance: usdcBalanceData?.formatted,
    maxAmount: usdcBalanceData ? Number(usdcBalanceData.formatted) : 0
  };
}